// Util tanggal/jam WIB bersama untuk alur peminjam (M2).
// Port dari prototypes/js/model.js: semua tanggal dimaknai sebagai waktu
// dinding Asia/Jakarta, bukan timezone perangkat.

const DAY_MS = 86400000;
const OFFSET_MS = 7 * 3600000;

/** Awal hari ini (00:00 WIB) sebagai epoch-ms. */
export function todayStartWib() {
  const now = new Date();
  const shifted = new Date(now.getTime() + OFFSET_MS);
  return Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate()) - OFFSET_MS;
}

/** "YYYY-MM-DD" -> awal hari itu (WIB), NaN bila tidak valid/kalender fiktif. */
export function parseDatePart(value) {
  if (typeof value !== "string") return NaN;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return NaN;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return NaN;
  const ms = Date.UTC(y, mo - 1, d) - OFFSET_MS;
  const back = new Date(ms + OFFSET_MS);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) return NaN;
  return ms;
}

/** "YYYY-MM-DDTHH:mm" (WIB) -> epoch-ms, NaN bila tidak valid. */
export function parseWibInput(value) {
  if (typeof value !== "string") return NaN;
  const [date, time] = value.split("T");
  const dayMs = parseDatePart(date);
  if (Number.isNaN(dayMs)) return NaN;
  const m = /^(\d{2}):(\d{2})$/.exec(time || "");
  if (!m) return NaN;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h > 23 || mi > 59) return NaN;
  return dayMs + h * 3600000 + mi * 60000;
}

export function formatDatePart(dayMs) {
  const d = new Date(dayMs + OFFSET_MS);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`;
}

/** epoch-ms -> "10 Des 2026, 09.00 WIB". */
export function formatWib(ms) {
  const d = new Date(ms);
  const date = d.toLocaleDateString("id-ID", { timeZone: "Asia/Jakarta", day: "numeric", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString("id-ID", { timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit" }).replace(":", ".");
  return `${date}, ${time} WIB`;
}

/** Tanggal pendek Indonesia untuk label di atas input jam. */
export function shortDay(dayMs) {
  return new Date(dayMs).toLocaleDateString("id-ID", {
    timeZone: "Asia/Jakarta", weekday: "short", day: "numeric", month: "short", year: "numeric",
  });
}

/** Nama hari panjang Indonesia untuk aria-label kalender. */
export function dayLabel(dayMs) {
  return new Date(dayMs).toLocaleDateString("id-ID", {
    timeZone: "Asia/Jakarta", weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
}

export function monthLabel(year, month) {
  return new Date(Date.UTC(year, month, 1)).toLocaleDateString("id-ID", {
    timeZone: "Asia/Jakarta", month: "long", year: "numeric",
  });
}

export function weekdayHeaders() {
  return ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];
}

export function shiftMonth(year, month, delta) {
  const total = year * 12 + month + delta;
  const y = Math.floor(total / 12);
  return { year: y, month: ((total % 12) + 12) % 12 };
}

/** Grid 42 sel Senin-pertama; hari lampau (WIB) disabled. */
export function buildMonthGrid(year, month, todayMs) {
  const first = Date.UTC(year, month, 1) - OFFSET_MS;
  const mondayIndex = (new Date(first + OFFSET_MS).getUTCDay() + 6) % 7;
  const start = first - mondayIndex * DAY_MS;
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const dayMs = start + i * DAY_MS;
    const d = new Date(dayMs + OFFSET_MS);
    cells.push({
      dayMs,
      inMonth: d.getUTCFullYear() === year && d.getUTCMonth() === month,
      disabled: dayMs < todayMs,
    });
  }
  return cells;
}

/** Status klik kalender: mulai baru, tutup rentang, atau mulai ulang. */
export function nextRange(sel, dayMs) {
  if (sel.start == null || (sel.start != null && sel.end != null)) return { start: dayMs, end: null };
  if (dayMs < sel.start) return { start: dayMs, end: null };
  return { start: sel.start, end: dayMs };
}

export function combineDateTime(datePart, timePart) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart || "")) return null;
  if (!/^\d{2}:\d{2}$/.test(timePart || "")) return null;
  const h = Number(timePart.slice(0, 2));
  const mi = Number(timePart.slice(3, 5));
  if (h > 23 || mi > 59) return null;
  return `${datePart}T${timePart}`;
}

/**
 * Validasi satu rentang penuh. Aturan tunggal untuk semua layar:
 * lengkap, valid, mulai di masa depan (WIB), kembali sesudah ambil
 * (hari sama boleh bila jam kembali lebih akhir).
 */
export function validateSchedule(pickupValue, returnValue, nowMs = Date.now()) {
  const errors = {};
  const startMs = parseWibInput(pickupValue);
  const endMs = parseWibInput(returnValue);
  if (!pickupValue || Number.isNaN(startMs)) errors.pickup = "Isi tanggal dan jam mulai yang valid.";
  else if (startMs <= nowMs) errors.pickup = "Jadwal mulai harus di masa depan (WIB).";
  if (!returnValue || Number.isNaN(endMs)) errors.return = "Isi tanggal dan jam selesai yang valid.";
  else if (!errors.pickup && endMs <= startMs) errors.return = "Jadwal selesai harus sesudah jadwal mulai.";
  return { ok: Object.keys(errors).length === 0, startMs, endMs, errors };
}
